import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { AdministrationMode, BSAFormula, ConcentrationBasis, IBWFormula, LabelSize, LabelVolume, Language, PatientIdentifier, PatientNameOrder, PatientTherapySequenceStatus, } from '../database/schema.js';
import { getSettings } from '../queries.js';
import { zql } from '../schema.js';
import { therapySequenceIncludesActiveSubstance } from '../util/patients-therapies.js';
import { assertCan, EventLogs } from '../utils.js';
import { addEventLog, EventLogResources } from './util.js';
import { itemsDetail } from '../util/event-log-items.js';
import { settingsItems } from './utils/settings-log-items.js';
import { statusChangeItems } from './patient/medicine-log-items.js';
import { getDispatchStatus, logAutomaticRelease } from './utils/auto-release.js';
import { updateProtocolDaySettings } from './utils/protocols.js';
export const UpdateSettingsSchema = z
    .object({
    licence: z.string().trim().nullable(),
    bsaFormula: BSAFormula.nullable(),
    ibwFormula: IBWFormula.nullable(),
    activeProtocolWarning: z.boolean().nullable(),
    doctorRelease: z.boolean().nullable(),
    deliveryAutoRelease: z.boolean().nullable(),
    autIdemLogic: z.boolean().nullable(),
    validationBypass: z.boolean().nullable(),
    prescribeByTradeName: z.boolean().nullable(),
    allowManualPatients: z.boolean().nullable(),
    allowManualStock: z.boolean().nullable(),
    markAdministeredOnDispatch: z.boolean().nullable(),
    administrationMode: AdministrationMode.nullable(),
    weightTolerance: z.number().positive().nullable(),
    weightAutoConfirm: z.boolean().nullable(),
    weightAutoConfirmDelayMs: z.int().min(500).max(10000).nullable(),
    // ui & branding
    language: Language.nullable(),
    loginText: z.string().nullable(),
    patientNameOrder: PatientNameOrder.nullable(),
    patientIdentifier: PatientIdentifier.nullable(),
    // rest vials
    allowVialReuse: z.boolean().nullable(),
    useVialSource: z.boolean().nullable(),
    minimumSaveableVolume: z.number().min(0).max(100).nullable(),
    dayZeroProtocol: z.boolean().nullable(),
    useScales: z.boolean().nullable(),
    recordScaleMemoryNumbers: z.boolean().nullable(),
    skipSolventStockCheck: z.boolean().nullable(),
    showHospitalPatientId: z.boolean().nullable(),
    // security
    // Min 15: the app checks the session every 5 minutes, shorter could log out active users.
    sessionDurationMinutes: z.int().min(15).max(43200),
    // printouts: label content
    prescribingDoctor: z.boolean().nullable(),
    hospitalNumber: z.boolean().nullable(),
    customerName: z.boolean().nullable(),
    wardNextToCustomerName: z.boolean().nullable(),
    manufacturer: z.boolean().nullable(),
    tradeNameBeforeSubstance: z.boolean().nullable(),
    concentration: z.boolean().nullable(),
    actualQuantity: z.boolean().nullable(),
    preparedBy: z.boolean().nullable(),
    pharmacyNameAndAddress: z.boolean().nullable(),
    additionalLabelBarcode: z.boolean().nullable(),
    labelVolume: LabelVolume.nullable(),
    bandTextCytotoxic: z.string().nullable(),
    bandTextNonCytotoxic: z.string().nullable(),
    labelCopies: z.int().min(1).nullable(),
    labelSize: LabelSize.nullable(),
    // printouts: preparation report
    printReportAutomatically: z.boolean().nullable(),
    includeLabelCopy: z.boolean().nullable(),
    includeEndControlSignatures: z.boolean().nullable(),
    showScaleMemoryNumbers: z.boolean().nullable(),
    showProductCodeColumn: z.boolean().nullable(),
    concentrationBasis: ConcentrationBasis.nullable(),
    // printouts: delivery and release
    deliveryNoteCopies: z.int().min(0).nullable(),
    releaseApprovalRequired: z.boolean().nullable(),
    signatureLinesOnPrintouts: z.boolean().nullable(),
})
    .partial();
export const settings = {
    update: defineMutator(UpdateSettingsSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'settings.manage');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        const prev = await getSettings(tx);
        const updated = {
            id: 1,
            ...args,
            ...userActions,
        };
        if (prev?.dayZeroProtocol !== args.dayZeroProtocol) {
            await updateProtocolDaySettings(tx, args.dayZeroProtocol !== undefined && args.dayZeroProtocol !== null ? args.dayZeroProtocol : true);
        }
        // Turning doctorRelease off removes the patient-arrival step; release every validated active-substance prescription waiting on arrival straight to production planning.
        if (args.doctorRelease === false && prev?.doctorRelease !== false) {
            await releaseValidatedTherapySequences(tx, ctx.user.id);
        }
        // Turning deliveryAutoRelease on removes the QA & dispatch step; release every preparation waiting there, as if it had just been produced.
        if (args.deliveryAutoRelease === true && prev?.deliveryAutoRelease !== true) {
            await releaseQaDispatchTherapySequences(tx, ctx.user.id, {
                markAdministeredOnDispatch: args.markAdministeredOnDispatch ?? prev?.markAdministeredOnDispatch,
            });
        }
        await tx.mutate.settings.upsert(updated);
        if (tx.location === 'server') {
            const patches = createPatch(prev, { ...prev, ...updated });
            const eventLogId = uuidv7();
            await addEventLog(tx, {
                id: eventLogId,
                resource: 'settings',
                resourceId: eventLogId, // `resourceId` accepts only uuids, but we use integer for the settings table; we'll only have one row in the settings table, so don't need to know the id
                action: 'update',
                event: EventLogs.SETTINGS_UPDATED,
                domain: 'configuration',
                status: 'OK',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                details: itemsDetail(settingsItems(prev, updated)),
            }, ctx);
        }
    }),
};
async function releaseValidatedTherapySequences(tx, userId) {
    const now = Date.now();
    const sequences = await tx.run(zql.patientTherapySequence
        .where('status', '=', PatientTherapySequenceStatus.enum.validating)
        .where('pharmacistValidatedAt', 'IS NOT', null)
        .where('deletedAt', 'IS', null)
        .related('medicine', medicine => medicine
        .where('deletedAt', 'IS', null)
        .related('activeSubstance')
        .related('prescribedName', prescribedName => prescribedName.related('drugCatalogItem'))));
    const ops = [];
    for (const sequence of sequences) {
        if (!therapySequenceIncludesActiveSubstance(sequence))
            continue;
        const update = {
            status: PatientTherapySequenceStatus.enum.production_planning,
            updatedAt: now,
            updatedBy: userId,
        };
        ops.push(() => tx.mutate.patientTherapySequence.update({ id: sequence.id, ...update }));
        if (tx.location === 'server') {
            ops.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: sequence.id,
                action: 'update',
                domain: 'prescription',
                event: EventLogs.PRESCRIPTION_STATUS_CHANGE,
                patches: createPatch({ status: sequence.status, updatedAt: sequence.updatedAt, updatedBy: sequence.updatedBy }, update),
                performedBy: userId,
                performedAt: now,
                details: {
                    therapyInternalId: sequence.internalId ?? '',
                    action: 'DOCTOR_RELEASE_DISABLED',
                    ...itemsDetail(statusChangeItems(sequence.status, update.status)),
                },
            }));
        }
    }
    await Promise.all(ops.map(op => op()));
}
async function releaseQaDispatchTherapySequences(tx, userId, settings) {
    const now = Date.now();
    const status = getDispatchStatus(settings);
    const sequences = await tx.run(zql.patientTherapySequence
        .where('status', '=', PatientTherapySequenceStatus.enum.qa_dispatch)
        .where('deletedAt', 'IS', null));
    const ops = [];
    for (const sequence of sequences) {
        ops.push(() => tx.mutate.patientTherapySequence.update({
            id: sequence.id,
            status,
            ...(status === PatientTherapySequenceStatus.enum.administered && { administeredAt: now }),
            updatedAt: now,
            updatedBy: userId,
        }));
        ops.push(() => logAutomaticRelease(tx, { sequenceId: sequence.id, therapyInternalId: sequence.internalId, status, userId, now }));
    }
    await Promise.all(ops.map(op => op()));
}
//# sourceMappingURL=settings.mutators.js.map